/**
 * Datadog RUM integration for the application.
 *
 * - **DatadogScript** — Renders the CDN loader inline script and calls DD_RUM.init()
 *   as early as possible in document <head>. Use in `_document.tsx` or RR Layout.
 *
 * - **DatadogInit** — Client-side fallback/enrichment via useEffect. Skips init when
 *   DatadogScript already initialized RUM; otherwise initializes with full JS config.
 */
/* eslint-disable react/boolean-prop-naming */
// Datadog's config uses boolean keys like trackUserInteractions

import { useEffect, useRef } from 'react';
import type { RumEvent } from '@datadog/browser-rum';

import {
  buildDatadogInitScript,
  buildDatadogScaLogsInitScript,
  DD_LOGS_INITIALIZED_FLAG,
  DD_RUM_INITIALIZED_FLAG,
  type DatadogInlineInitConfig,
} from './build-datadog-init-script';

const DEFAULT_ALLOWED_TRACING_URLS_PATTERN =
  /^https:\/\/(?:[a-z0-9-]+\.)*hilton\.com(?::\d+)?(?:[/?#]|$)/;

export type DatadogScriptProps = Omit<DatadogInlineInitConfig, 'applicationId' | 'clientToken'> & {
  applicationId?: string;
  clientToken?: string;
};

export type DatadogInitProps = {
  applicationId: string;
  clientToken: string;
  site?: string;
  service?: string;
  env?: string;
  version?: string;
  sessionSampleRate?: number;
  sessionReplaySampleRate?: number;
  trackUserInteractions?: boolean;
  trackResources?: boolean;
  trackLongTasks?: boolean;
  enableScaLogs?: boolean;
  allowedTracingUrlsPattern?: string;
  [key: string]: unknown;
  applicationName?: string;
  applicationUniqueId?: string;
};

declare global {
  interface Window {
    [DD_RUM_INITIALIZED_FLAG]?: boolean;
    [DD_LOGS_INITIALIZED_FLAG]?: boolean;
  }
}

function getDatadogCdnSite(site: string): string {
  const siteMap: Record<string, string> = {
    'datadoghq.com': 'us1',
  };

  if (site.length <= 4 && !site.includes('.')) {
    return site;
  }

  return siteMap[site] || 'us1';
}

function enrichDatadogContext(applicationName?: string, applicationUniqueId?: string) {
  setTabId();
  setAppContext(applicationName, applicationUniqueId);
  setLocaleProperty();
}

function initScaLogs(
  clientToken: string,
  site: string,
  service?: string,
  env?: string,
  version?: string
) {
  window.DD_LOGS?.onReady(() => {
    if (window[DD_LOGS_INITIALIZED_FLAG]) {
      return;
    }
    window[DD_LOGS_INITIALIZED_FLAG] = true;

    window.DD_LOGS?.init({
      clientToken,
      site,
      ...(service ? { service } : {}),
      ...(env ? { env } : {}),
      ...(version ? { version } : {}),
      forwardErrorsToLogs: false,
      sessionSampleRate: 100,
    });
    window.DD_LOGS?.createLogger('sca-graphql', { level: 'info' });
  });
}

/**
 * Client-side Datadog RUM enrichment and fallback initialization.
 * Prefer initializing via DatadogScript in document head; this component
 * skips DD_RUM.init() when inline init already ran.
 */
export function DatadogInit({
  applicationId,
  clientToken,
  site = 'datadoghq.com',
  service,
  env,
  version,
  sessionSampleRate = 100,
  sessionReplaySampleRate = 0,
  trackUserInteractions = true,
  trackResources = true,
  trackLongTasks = true,
  enableScaLogs = false,
  applicationName,
  applicationUniqueId,
  allowedTracingUrlsPattern,
  ...rest
}: DatadogInitProps) {
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) {
      return;
    }

    hasRunRef.current = true;

    window.DD_RUM?.onReady(() => {
      if (window[DD_RUM_INITIALIZED_FLAG]) {
        enrichDatadogContext(applicationName, applicationUniqueId);
        return;
      }

      window[DD_RUM_INITIALIZED_FLAG] = true;

      const initConfig: Record<string, unknown> = {
        applicationId,
        clientToken,
        site,
        sessionSampleRate,
        sessionReplaySampleRate,
        trackUserInteractions,
        trackResources,
        trackLongTasks,
        enablePrivacyForActionName: true,
        enableSessionReplay: false,
        allowedGraphQlUrls: [/https:\/\/.*\.hilton\.com\/graphql/],
        allowedTracingUrls: [allowedTracingUrlsPattern || DEFAULT_ALLOWED_TRACING_URLS_PATTERN],
        enableExperimentalFeatures: ['feature_flags', 'feature_operation_vital'],
        defaultPrivacyLevel: 'allow',
        beforeSend: (event: RumEvent) => {
          // Cross origin 3rd party scripts can throw a "Script error." which is not actionable and should be ignored.
          if (event.type === 'error' && event.error?.message === 'Script error.') {
            return false;
          }
          if (event.type === 'resource' && event.resource.url.includes('graphql')) {
            const url = new URL(event.resource.url);
            const opName = url.searchParams.get('operationName');
            event.resource.url = `${service}-${opName}`;
          }
          return true;
        },
        ...rest,
      };

      if (service) {
        initConfig['service'] = service;
      }
      if (env) {
        initConfig['env'] = env;
      }
      if (version) {
        initConfig['version'] = version;
      }

      window.DD_RUM?.init(initConfig);
      enrichDatadogContext(applicationName, applicationUniqueId);
    });

    if (enableScaLogs) {
      initScaLogs(clientToken, site, service, env, version);
    }
  }, []);

  return null;
}

function setAppContext(applicationName?: string, applicationUniqueId?: string) {
  if (applicationName) {
    window.DD_RUM?.setGlobalContextProperty('app-name', applicationName);
  }

  if (applicationUniqueId) {
    window.DD_RUM?.setGlobalContextProperty('unique-id', applicationUniqueId);
  }
}

function setLocaleProperty() {
  let locale = window.location.pathname.split('/')[1];
  if (locale !== 'en') {
    locale = 'non-en';
  }
  window.DD_RUM?.setGlobalContextProperty('locale', locale);
}

function setTabId() {
  const existingTabId = sessionStorage.getItem('custTabId');
  if (!existingTabId) {
    const newTabId = crypto.randomUUID();
    sessionStorage.setItem('custTabId', newTabId);
    window.DD_RUM?.setGlobalContextProperty('custTabId', newTabId);
  } else {
    window.DD_RUM?.setGlobalContextProperty('custTabId', existingTabId);
  }
}

/**
 * Loads the Datadog RUM SDK via CDN and initializes RUM inline in document head.
 */
export function DatadogScript({
  applicationId,
  clientToken,
  site = 'datadoghq.com',
  service,
  env,
  version,
  sessionSampleRate = 100,
  sessionReplaySampleRate = 0,
  trackUserInteractions = true,
  trackResources = true,
  trackLongTasks = true,
  enableScaLogs = false,
  allowedTracingUrlsPattern,
  applicationName,
  applicationUniqueId,
}: DatadogScriptProps) {
  if (!applicationId || !clientToken) {
    return null;
  }

  const cdnSite = getDatadogCdnSite(site);
  const cdnUrl = `https://www.datadoghq-browser-agent.com/${cdnSite}/v7/datadog-rum.js`;
  const logsCdnUrl = `https://www.datadoghq-browser-agent.com/${cdnSite}/v7/datadog-logs.js`;

  const inlineInitConfig: DatadogInlineInitConfig = {
    applicationId,
    clientToken,
    site,
    service,
    env,
    version,
    sessionSampleRate,
    sessionReplaySampleRate,
    trackUserInteractions,
    trackResources,
    trackLongTasks,
    enableScaLogs,
    allowedTracingUrlsPattern,
    applicationName,
    applicationUniqueId,
  };

  const initScript = buildDatadogInitScript(inlineInitConfig);
  const scaLogsInitScript = enableScaLogs ? buildDatadogScaLogsInitScript(inlineInitConfig) : '';

  const cdnLoaderScript = (url: string, globalName: string, extra = '') =>
    `(function(h,o,u,n,d){h=h[d]=h[d]||{q:[],onReady:function(c){h.q.push(c)}};d=o.createElement(u);d.async=1;d.src=n;n=o.getElementsByTagName(u)[0];n.parentNode.insertBefore(d,n)})(window,document,'script','${url}','${globalName}');${extra}`.trim();

  return (
    <>
      <script dangerouslySetInnerHTML={{ __html: cdnLoaderScript(cdnUrl, 'DD_RUM', initScript) }} />
      {enableScaLogs ? (
        <script
          dangerouslySetInnerHTML={{
            __html: cdnLoaderScript(logsCdnUrl, 'DD_LOGS', scaLogsInitScript),
          }}
        />
      ) : null}
    </>
  );
}

export default DatadogScript;
